import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Megaphone, ArrowRight, ExternalLink } from 'lucide-react';
import { useChannel } from '../contexts/ChannelContext';
import { findSponsor } from '../data/sponsors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter151 · Ads globally disabled — the founder decided users should
// never see sponsored slots. Every call site (Homepage leaderboard,
// PersonaActionPanel brand preview, story pages, etc.) still renders
// this component, but we short-circuit to `null` so nothing paints.
// The admin dashboard tabs that MANAGE ads (SponsoredAdsTab,
// AdSlotsTab) still work — the founder can re-enable by editing this
// constant back to `false`.
const ADS_DISABLED_GLOBALLY = true;

const AdSlot = ({ id, label = 'Featured Partner Slot', format = '970 × 250', position = 'leaderboard' }) => {
  const channel = useChannel();
  const staticSponsor = findSponsor(channel.id, id);

  // ── DB-backed slot (managed via Admin → Ad Slots) ──
  // Takes priority over the static sponsors.js entry so jay can swap a
  // banner without a code push. `null` while loading or no match.
  const [dbSlot, setDbSlot] = useState(null);
  useEffect(() => {
    if (ADS_DISABLED_GLOBALLY) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/ad-slots/active`, { params: { slot_key: id, channel: channel.id } })
      .then((res) => { if (!cancelled) setDbSlot(res.data || null); })
      .catch(() => { /* fall back silently to static / placeholder */ });
    return () => { cancelled = true; };
  }, [id, channel.id]);

  // Global kill-switch — return null before any render logic runs.
  if (ADS_DISABLED_GLOBALLY) return null;

  // Render order: DB-managed → static sponsor → empty placeholder.
  const sponsor = dbSlot
    ? {
        // Adapt DB shape → the existing render-time `sponsor` shape so the
        // big rendered block below stays unchanged.
        id: dbSlot.id,
        name: dbSlot.sponsor_name || dbSlot.headline || 'Sponsor',
        url: dbSlot.link_url || '#',
        logo: dbSlot.logo_url || '',
        tagline: dbSlot.tagline || '',
        body: dbSlot.body || '',
        ctaLabel: dbSlot.cta_label || 'Learn more',
        accentFrom: dbSlot.accent_from || '#fbbf24',
        accentTo: dbSlot.accent_to || '#f59e0b',
        textColor: dbSlot.text_color || '#000000',
        ctaBg: dbSlot.cta_bg || '#000000',
        ctaText: dbSlot.cta_text || '#ffffff',
        sideImage: dbSlot.image_url || '',
        sideImageAlt: dbSlot.sponsor_name || '',
        // The Headline ALWAYS takes the slot's "name" position. If a
        // separate headline was provided we override `name` with it.
        ...(dbSlot.headline ? { name: dbSlot.headline } : {}),
      }
    : staticSponsor;

  // Click tracking — when the slot has a DB id we use the /api/r/{id}
  // redirect as the link target itself, so the browser follows our 302 to
  // the destination. That guarantees attribution even when sendBeacon is
  // unreliable (Safari, in-app webviews). Fallback to direct URL for the
  // legacy static sponsors.js entries (no DB id to track).
  const trackedHref = dbSlot?.id
    ? `${API}/r/${dbSlot.id}`
    : (sponsor?.url || '#');
  const trackClick = () => {
    // Kept as a safety net for any environment where the <a> click is
    // intercepted before navigation. No-ops when dbSlot is null.
    if (dbSlot?.id) {
      try { navigator.sendBeacon?.(`${API}/ad-slots/${dbSlot.id}/click`); } catch { /* ignore */ }
    }
  };

  // ============= ACTIVE SPONSOR =============
  if (sponsor) {
    const textColor = sponsor.textColor || '#fff';
    const eyebrowColor = sponsor.eyebrowColor || textColor;
    const ctaBg = sponsor.ctaBg || '#ffffff';
    const ctaText = sponsor.ctaText || '#000000';
    const logoFilter = sponsor.logoFilter || 'none';

    return (
      <section className="py-8 bg-[#f7f6f3]" data-testid={`ad-slot-${id}-sponsored`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-bold flex items-center justify-between">
            <span>Sponsored by {sponsor.name}</span>
            <span className="text-gray-300">Ad</span>
          </div>

          <a
            href={trackedHref}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={trackClick}
            className="group block relative rounded-2xl overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-0.5"
            style={{
              backgroundImage: `linear-gradient(120deg, ${sponsor.accentFrom} 0%, ${sponsor.accentTo} 100%)`,
              color: textColor,
            }}
            data-testid={`sponsor-cta-${sponsor.id}`}
          >
            {/* Optional retro pixel grid overlay */}
            {sponsor.retroPixelGrid && (
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
                  backgroundSize: '24px 24px',
                }}
              />
            )}

            {/* Glow accent */}
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(circle at 90% 25%, rgba(255,255,255,0.25), transparent 55%)'
              }}
            />

            <div className="relative grid grid-cols-12 items-center gap-4 sm:gap-6 px-5 sm:px-8 py-6 sm:py-8 min-h-[160px]">
              {/* LEFT — sideImage (founder photo) OR logo */}
              {sponsor.sideImage ? (
                <div className="col-span-3 sm:col-span-2 flex flex-col items-center">
                  <div
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden ring-2 shadow-lg"
                    style={{ borderColor: textColor, boxShadow: '0 6px 24px rgba(0,0,0,0.18)' }}
                  >
                    <img loading="lazy" decoding="async"
                      src={sponsor.sideImage}
                      alt={sponsor.sideImageAlt || sponsor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {sponsor.sideImageCaption && (
                    <p className="text-[10px] font-bold mt-2 opacity-80 text-center" style={{ color: textColor }}>
                      {sponsor.sideImageCaption}
                    </p>
                  )}
                </div>
              ) : (
                <div className="col-span-3 sm:col-span-2 flex justify-start">
                  <div
                    className="rounded-2xl px-4 py-3 inline-flex items-center justify-center"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    {sponsor.logo ? (
                      <img loading="lazy" decoding="async"
                        src={sponsor.logo}
                        alt={`${sponsor.name} logo`}
                        className="h-8 sm:h-10 w-auto object-contain max-w-[120px]"
                        style={{ filter: logoFilter }}
                      />
                    ) : (
                      <span className="font-display text-xl sm:text-2xl tracking-tight" style={{ color: textColor }}>
                        {sponsor.name.split(' ')[0]}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* MIDDLE — copy + (optional) logo if sideImage was used */}
              <div className="col-span-9 sm:col-span-7">
                {sponsor.sideImage && sponsor.logo && (
                  <img loading="lazy" decoding="async"
                    src={sponsor.logo}
                    alt={`${sponsor.name} logo`}
                    className="h-5 sm:h-6 w-auto object-contain mb-2 opacity-90"
                    style={{ filter: logoFilter }}
                  />
                )}
                <p
                  className="font-italic text-[10px] sm:text-xs uppercase tracking-widest mb-1.5"
                  style={{ color: eyebrowColor, opacity: 0.95 }}
                >
                  {sponsor.tagline}
                </p>
                <h3 className="font-display text-xl sm:text-2xl lg:text-3xl uppercase leading-tight mb-2">
                  {sponsor.name}
                </h3>
                <p className="font-body text-xs sm:text-sm leading-relaxed max-w-md" style={{ opacity: 0.9 }}>
                  {sponsor.body}
                </p>
              </div>

              {/* RIGHT — product image (if present) AND CTA */}
              <div className="col-span-12 sm:col-span-3 flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2 justify-between sm:justify-center">
                {sponsor.productImage && (
                  <img loading="lazy" decoding="async"
                    src={sponsor.productImage}
                    alt={`${sponsor.name} product`}
                    className="h-20 sm:h-24 w-auto object-contain drop-shadow-2xl hidden sm:block"
                  />
                )}
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition group-hover:scale-105"
                  style={{ backgroundColor: ctaBg, color: ctaText }}
                >
                  {sponsor.ctaLabel} <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            </div>
          </a>

          <p className="text-[10px] text-gray-400 mt-2 text-right">
            Interested in this slot?{' '}
            <Link
              to={channel.id === 'flyt' ? '/flyt/brands' : '/brands'}
              className="underline hover:text-black"
            >
              Become a partner →
            </Link>
          </p>
        </div>
      </section>
    );
  }

  // ============= EMPTY PLACEHOLDER =============
  return (
    <section className="py-10 bg-[#f7f6f3]" data-testid={`ad-slot-${id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2 font-bold">Advertisement</div>
        <Link
          to={channel.id === 'flyt' ? '/flyt/brands' : '/brands'}
          className="group block relative rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-blue-400 bg-white hover:bg-blue-50/30 transition-all"
          data-testid={`ad-slot-cta-${id}`}
        >
          <div className="px-6 sm:px-10 py-10 sm:py-14 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition">
                <Megaphone className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition" />
              </div>
              <div>
                <p className="font-display text-xl uppercase tracking-tight text-gray-700 group-hover:text-black transition">
                  {label}
                </p>
                <p className="font-body text-xs text-gray-500 mt-1">
                  {format} · {position} · Reserve this slot
                </p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white text-sm font-bold group-hover:bg-blue-600 transition">
              Become a Partner <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default AdSlot;
